import { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography, IconButton, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import dayjs, { type Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { PmCard, PmPill } from '../../../components/pm';
import { applyDayDelta, type DragMode } from '../lib/ganttDrag';
import { apLabel } from '../lib/apLabel';
import { meetingTypeSymbol } from '../lib/meetingTypeSymbol';
import { apDepth, type ApNumberNode } from '../lib/apNumbering';
import { interleaveMilestones } from '../lib/interleaveMilestones';
import { displayProgress, AP_DONE_STATUS } from '../lib/apStatus';

dayjs.extend(isoWeek);

export interface GanttArbeitspaket {
  id: string;
  nr: string;
  titel: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string;
  progress: number;
  is_milestone: boolean;
  /** Eltern-AP (PHQ-85): steuert die Einrückung des Namens je Hierarchie-Ebene. */
  parent_id?: string | null;
}

export interface GanttMilestone {
  id: string;
  name: string;
  date: string | null;
}

export interface GanttMeeting {
  id: string;
  titel: string | null;
  datum: string;
  typ?: string | null;
}

export interface GanttOffenerPunkt {
  id: string;
  titel: string | null;
  faellig: string | null;
}

interface PmProjectGanttProps {
  arbeitspakete: GanttArbeitspaket[];
  milestones: GanttMilestone[];
  meetings: GanttMeeting[];
  offenePunkte: GanttOffenerPunkt[];
  onApMove?: (id: string, newStart: string, newEnd: string) => void;
  /** PHQ-101: Klick auf einen Meilenstein (Zeile/Linie), z. B. zum Bearbeiten. */
  onMilestoneClick?: (id: string) => void;
}

type Zoom = 'day' | 'week' | 'month';

/** Anzeigename eines AP: der Titel, ohne Titel ersatzweise die Nummer. */
function apTitel(ap: GanttArbeitspaket): string {
  const t = (ap.titel ?? '').trim();
  return t.length > 0 ? t : apLabel(ap.nr);
}

/** SVG-Text kennt kein Ellipsis — gekürzt wird nach geschätzter Zeichenbreite. */
function kuerze(text: string, maxZeichen: number): string {
  return text.length > maxZeichen ? `${text.slice(0, Math.max(1, maxZeichen - 1))}…` : text;
}

/** Ein Eintrag im Termin-Streifen unter den AP-Zeilen (Meilenstein, Meeting, offener Punkt). */
interface FooterItem {
  key: string;
  kind: 'ms' | 'mt' | 'op';
  x: number;
  lane: number;
  label: string;
  tooltip: string;
  symbol: string;
}

const FOOTER_LANE_H = 18;
const FOOTER_CHAR_W = 5.6;

/**
 * Verteilt die Termin-Einträge auf Bahnen, damit sich ihre Titel (PHQ-82/84) nicht
 * überlappen: je Eintrag die erste Bahn, deren letzter Eintrag links davon endet.
 */
function layoutFooter(items: Array<Omit<FooterItem, 'lane'>>): { items: FooterItem[]; lanes: number } {
  const sorted = [...items].sort((a, b) => a.x - b.x);
  const laneEnd: number[] = [];
  const out: FooterItem[] = sorted.map((it) => {
    const width = 22 + it.label.length * FOOTER_CHAR_W;
    let lane = laneEnd.findIndex((end) => end + 6 <= it.x - 8);
    if (lane < 0) { lane = laneEnd.length; laneEnd.push(0); }
    laneEnd[lane] = it.x - 8 + width;
    return { ...it, lane };
  });
  return { items: out, lanes: Math.max(1, laneEnd.length) };
}

const STATUS_COLOR: Record<string, string> = {
  'Offen': 'var(--pm-gray-400)',
  'In Arbeit': 'var(--pm-info)',
  'Erledigt': 'var(--pm-success)',
  'Blockiert': 'var(--pm-error)',
};

interface RangeCalc {
  start: Dayjs;
  end: Dayjs;
  cellW: number;
  totalCells: number;
  rowH: number;
}

function dayToX(date: string | null, range: RangeCalc): number | null {
  if (!date) return null;
  const d = dayjs(date);
  const days = d.diff(range.start, 'day');
  return days * (range.cellW / cellUnitDays(range));
}

function cellUnitDays(_range: RangeCalc): number {
  // jeder cell zeigt einen Tag — bei Wochen-Zoom werden mehrere Tage in einer cell zusammengefasst,
  // aber die X-Berechnung bleibt tagesgenau.
  return 1;
}

/**
 * PM Projektplan-Gantt — SVG-basiert, kein frappe-gantt.
 * Render: AP-Balken horizontal mit Drag-Move, Meilensteine ◊, Meetings ●,
 * Offene Punkte ✱, aktuelle KW als gelbe Vertikal-Linie.
 */
export function PmProjectGantt({
  arbeitspakete,
  milestones,
  meetings,
  offenePunkte,
  onApMove,
  onMilestoneClick,
}: PmProjectGanttProps) {
  const [zoom, setZoom] = useState<Zoom>('week');
  const svgRef = useRef<SVGSVGElement>(null);
  const [drag, setDrag] = useState<{ id: string; mode: DragMode; startMouseX: number } | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  const range: RangeCalc = useMemo(() => {
    const allDates: string[] = [];
    arbeitspakete.forEach((a) => { if (a.start_date) allDates.push(a.start_date); if (a.end_date) allDates.push(a.end_date); });
    milestones.forEach((m) => { if (m.date) allDates.push(m.date); });
    meetings.forEach((m) => { if (m.datum) allDates.push(m.datum); });
    offenePunkte.forEach((o) => { if (o.faellig) allDates.push(o.faellig); });

    let start = dayjs().startOf('week');
    let end = dayjs().add(8, 'week').endOf('week');
    if (allDates.length > 0) {
      const sorted = allDates.sort();
      start = dayjs(sorted[0]).startOf('week').subtract(7, 'day');
      end = dayjs(sorted[sorted.length - 1]).endOf('week').add(7, 'day');
    }
    const totalDays = end.diff(start, 'day') + 1;
    const cellW = zoom === 'day' ? 32 : zoom === 'week' ? 16 : 7;
    const rowH = 32;
    return { start, end, cellW, totalCells: totalDays, rowH };
  }, [arbeitspakete, milestones, meetings, offenePunkte, zoom]);

  const W = Math.max(800, range.totalCells * range.cellW + 200);
  const headerH = 48;
  const leftPad = 180;

  // PHQ-85: Hierarchie-Ebene je AP für die Einrückung der Namen.
  const depthById = useMemo(() => {
    const nodes: ApNumberNode[] = arbeitspakete.map((a) => ({ id: a.id, nr: a.nr, parent_id: a.parent_id ?? null }));
    const byId = new Map(nodes.map((n) => [n.id, n]));
    return new Map(nodes.map((n) => [n.id, apDepth(n, byId)]));
  }, [arbeitspakete]);

  // Termin-Streifen (PHQ-82/84): Symbol + Titel je Meilenstein, Meeting und offenem Punkt.
  const footer = useMemo(() => {
    const raw: Array<Omit<FooterItem, 'lane'>> = [];
    meetings.forEach((m) => {
      const x = dayToX(m.datum, range);
      if (x == null) return;
      raw.push({
        key: `mt-${m.id}`, kind: 'mt', x: leftPad + x,
        label: kuerze((m.titel ?? '').trim() || m.typ || 'Termin', 28),
        tooltip: `${m.typ || 'Termin'}: ${m.titel ?? '—'} (${m.datum})`, symbol: meetingTypeSymbol(m.typ),
      });
    });
    offenePunkte.forEach((o) => {
      const x = dayToX(o.faellig, range);
      if (x == null) return;
      raw.push({ key: `op-${o.id}`, kind: 'op', x: leftPad + x, label: kuerze((o.titel ?? '').trim() || 'Offener Punkt', 28), tooltip: `Offener Punkt: ${o.titel ?? '—'} (${o.faellig})`, symbol: '✱' });
    });
    return layoutFooter(raw);
  }, [meetings, offenePunkte, range]);

  // PHQ-101: Meilensteine als farbig abgesetzte Zeilen zwischen den APs (nach Datum einsortiert).
  const rows = useMemo(
    () => interleaveMilestones(
      arbeitspakete,
      milestones.filter((m) => m.date).map((m) => ({ id: m.id, name: m.name, deadline: m.date })),
    ),
    [arbeitspakete, milestones],
  );

  const footerTop = headerH + rows.length * range.rowH + 16;
  const H = footerTop + footer.lanes * FOOTER_LANE_H + 14;

  // Header: Wochen oder Monate
  const headers = useMemo(() => {
    const out: Array<{ x: number; label: string; isHighlight: boolean }> = [];
    let cursor = range.start.clone();
    while (cursor.isBefore(range.end)) {
      const x = leftPad + cursor.diff(range.start, 'day') * range.cellW;
      const label = zoom === 'month'
        ? cursor.format('MMM YY')
        : `KW ${cursor.isoWeek()}`;
      const isHighlight = cursor.isoWeek() === dayjs().isoWeek() && cursor.year() === dayjs().year();
      out.push({ x, label, isHighlight });
      cursor = zoom === 'month' ? cursor.add(1, 'month').startOf('month') : cursor.add(7, 'day');
    }
    return out;
  }, [range, zoom]);

  const todayX = useMemo(() => {
    const now = dayjs();
    if (now.isBefore(range.start) || now.isAfter(range.end)) return null;
    return leftPad + now.diff(range.start, 'day') * range.cellW;
  }, [range]);

  const handleMouseDown = (e: React.MouseEvent, ap: GanttArbeitspaket, mode: DragMode) => {
    if (!onApMove) return;
    e.stopPropagation();
    setDrag({ id: ap.id, mode, startMouseX: e.clientX });
    setDragOffset(0);
  };

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!drag) return;
    setDragOffset(e.clientX - drag.startMouseX);
  }, [drag]);

  const handleMouseUp = useCallback(() => {
    if (!drag || !onApMove) { setDrag(null); setDragOffset(0); return; }
    const deltaDays = Math.round(dragOffset / range.cellW);
    if (deltaDays !== 0) {
      const ap = arbeitspakete.find((a) => a.id === drag.id);
      if (ap && ap.start_date && ap.end_date) {
        const res = applyDayDelta(ap.start_date, ap.end_date, drag.mode, deltaDays);
        onApMove(drag.id, res.start, res.end);
      }
    }
    setDrag(null);
    setDragOffset(0);
  }, [drag, dragOffset, range.cellW, arbeitspakete, onApMove]);

  return (
    <PmCard>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Projektplan</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Stack direction="row" spacing={1.5} sx={{ fontSize: '0.72rem', color: 'var(--pm-fg-muted)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ color: 'var(--pm-warning)', fontSize: '0.95rem', lineHeight: 1 }}>◊</Box>
              Meilenstein
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ color: 'var(--pm-info)', fontSize: '0.95rem', lineHeight: 1 }}>●</Box>
              Meeting
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ color: 'var(--pm-error)', fontSize: '0.95rem', lineHeight: 1 }}>✱</Box>
              Offener Punkt
            </Box>
          </Stack>
          <Tooltip title="Zoom auf Tage">
            <IconButton size="small" onClick={() => setZoom('day')} sx={{ color: zoom === 'day' ? 'var(--rittec-petrol)' : 'var(--pm-fg-subtle)' }}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <PmPill tone={zoom === 'week' ? 'primary' : 'neutral'}>
            <Box component="button" onClick={() => setZoom('week')} sx={{ border: 0, background: 'none', color: 'inherit', cursor: 'pointer', font: 'inherit' }}>
              KW
            </Box>
          </PmPill>
          <Tooltip title="Zoom auf Monate">
            <IconButton size="small" onClick={() => setZoom('month')} sx={{ color: zoom === 'month' ? 'var(--rittec-petrol)' : 'var(--pm-fg-subtle)' }}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <svg
          ref={svgRef}
          width={W}
          height={H}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ minWidth: 800, userSelect: 'none', display: 'block' }}
        >
          {/* Header: AP-Name-Spalte */}
          <rect x={0} y={0} width={leftPad} height={headerH} fill="var(--pm-bg-deep)" />
          <text x={12} y={headerH / 2 + 4} fontSize="11" fill="var(--pm-fg-subtle)" fontWeight={700} letterSpacing="0.08em">
            ARBEITSPAKET
          </text>

          {/* Header: Zeitskala */}
          <rect x={leftPad} y={0} width={W - leftPad} height={headerH} fill="var(--pm-bg-deep)" />
          {headers.map((h, i) => (
            <g key={i}>
              <line x1={h.x} y1={0} x2={h.x} y2={footerTop - 4} stroke="var(--pm-bg-muted)" strokeWidth={h.isHighlight ? 0 : 0.5} />
              <text x={h.x + 4} y={headerH - 14} fontSize="10" fill={h.isHighlight ? 'var(--pm-warning)' : 'var(--pm-fg-muted)'} fontWeight={h.isHighlight ? 700 : 500}>
                {h.label}
              </text>
            </g>
          ))}

          {/* Aktuelle KW als gelbe Vertikal-Linie */}
          {todayX != null && (
            <>
              <line x1={todayX} y1={headerH} x2={todayX} y2={footerTop - 4} stroke="var(--pm-warning)" strokeWidth={2} opacity={0.7} />
              <circle cx={todayX} cy={headerH - 6} r={4} fill="var(--pm-warning)" />
            </>
          )}

          {/* Zeilen-Hintergrund */}
          {rows.map((row, i) => (
            <rect
              key={i}
              x={0}
              y={headerH + i * range.rowH}
              width={W}
              height={range.rowH}
              fill={row.kind === 'ms' ? 'var(--pm-warning)' : i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'}
              fillOpacity={row.kind === 'ms' ? 0.16 : 1}
            />
          ))}

          {/* PHQ-101: Meilensteine als Ziellinie über die ganze Diagrammhöhe, mit Titel am Kopf der Zeile. */}
          {rows.map((row, i) => {
            if (row.kind !== 'ms') return null;
            const mx = dayToX(row.ms.deadline, range);
            if (mx == null) return null;
            const x = leftPad + mx;
            const cy = headerH + i * range.rowH + range.rowH / 2;
            const click = onMilestoneClick ? () => onMilestoneClick(row.ms.id) : undefined;
            return (
              <g key={`msl-${row.ms.id}`} data-testid="gantt-ms" onClick={click} style={{ cursor: click ? 'pointer' : 'default' }}>
                <title>{`Meilenstein: ${row.ms.name} (${row.ms.deadline})`}</title>
                <line data-testid="gantt-ms-line" x1={x} y1={headerH} x2={x} y2={H} stroke="var(--pm-warning)" strokeWidth={2} strokeDasharray="6 3" />
                <polygon
                  points={`${x - 8},${cy} ${x},${cy - 9} ${x + 8},${cy} ${x},${cy + 9}`}
                  fill="var(--pm-warning)"
                  stroke="var(--pm-bg-deep)"
                />
                <text x={x + 12} y={cy + 4} fontSize="11" fontWeight={700} fill="var(--pm-fg-base)">{kuerze(row.ms.name, 40)}</text>
              </g>
            );
          })}

          {/* AP-Namen links — PHQ-57/58: nur der Titel. Die Nummer stand hier fest
              auf 62px Breite; Nummern mit Projektkürzel („ARTURON erp-AP-1") liefen
              damit in den Titel. Die Nummer bleibt im Tooltip erreichbar. */}
          {rows.map((row, i) => {
            if (row.kind === 'ms') {
              return (
                <text key={`name-ms-${row.ms.id}`} x={12} y={headerH + i * range.rowH + range.rowH / 2 + 4} fontSize="11" fontWeight={700} fill="var(--pm-fg-base)">
                  {`◊ ${kuerze(row.ms.name, Math.floor((leftPad - 32) / 6.5))}`}
                </text>
              );
            }
            const ap = row.ap;
            return (
            <g key={`name-${ap.id}`}>
              <title>{`${apLabel(ap.nr)} — ${ap.titel ?? ''}`}</title>
              <text
                x={12 + (depthById.get(ap.id) ?? 0) * 12}
                y={headerH + i * range.rowH + range.rowH / 2 + 4}
                fontSize="11"
                fill="var(--pm-fg-base)"
                fontWeight={(depthById.get(ap.id) ?? 0) > 0 ? 500 : 600}
              >
                {kuerze(apTitel(ap), Math.floor((leftPad - 20 - (depthById.get(ap.id) ?? 0) * 12) / 6.5))}
              </text>
            </g>
            );
          })}

          {/* AP-Balken */}
          {rows.map((row, i) => {
            if (row.kind !== 'ap') return null;
            const ap = row.ap;
            const x1 = dayToX(ap.start_date, range);
            const x2 = dayToX(ap.end_date, range);
            if (x1 == null || x2 == null) return null;
            const dmode = drag?.id === ap.id ? drag.mode : null;
            const offset = dmode ? dragOffset : 0;
            let xStart = leftPad + x1;
            let xEnd = leftPad + x2;
            if (dmode === 'move') { xStart += offset; xEnd += offset; }
            else if (dmode === 'start') { xStart += offset; }
            else if (dmode === 'end') { xEnd += offset; }
            const width = Math.max(8, xEnd - xStart);
            const y = headerH + i * range.rowH + 6;
            const h = range.rowH - 12;
            const color = STATUS_COLOR[ap.status] ?? 'var(--pm-gray-400)';
            // PHQ-87: erledigte APs zeigen immer 100 % (Balken grün, durchgehend gefüllt).
            const shownProgress = displayProgress(ap.status, ap.progress);
            const isDone = ap.status === AP_DONE_STATUS;
            const progressW = (width * Math.min(100, Math.max(0, shownProgress))) / 100;
            return (
              <g key={`bar-${ap.id}`} style={{ cursor: onApMove ? (drag ? 'grabbing' : 'grab') : 'default' }}>
                <rect
                  x={xStart}
                  y={y}
                  width={width}
                  height={h}
                  rx={4}
                  fill="var(--pm-bg-subtle)"
                  stroke={color}
                  strokeWidth={1.5}
                  onMouseDown={(e) => handleMouseDown(e, ap, 'move')}
                />
                <rect
                  x={xStart}
                  y={y}
                  width={progressW}
                  height={h}
                  rx={4}
                  fill={color}
                  opacity={isDone ? 0.8 : 0.55}
                  pointerEvents="none"
                />
                <text
                  x={xStart + 6}
                  y={y + h / 2 + 4}
                  fontSize="10"
                  fill="var(--pm-fg-base)"
                  fontWeight={600}
                  pointerEvents="none"
                >
                  {kuerze(`${isDone ? '✓ ' : ''}${apTitel(ap)} (${shownProgress}%)`, Math.max(4, Math.floor((width - 12) / 6)))}
                </text>
                {onApMove && (
                  <>
                    {/* Kanten-Handles: linke = Start, rechte = Ende anpassen */}
                    <rect x={xStart - 2} y={y} width={6} height={h} fill="transparent" style={{ cursor: 'ew-resize' }} onMouseDown={(e) => handleMouseDown(e, ap, 'start')} />
                    <rect x={xEnd - 4} y={y} width={6} height={h} fill="transparent" style={{ cursor: 'ew-resize' }} onMouseDown={(e) => handleMouseDown(e, ap, 'end')} />
                  </>
                )}
                {ap.is_milestone && (
                  <polygon
                    points={`${xEnd},${y - 4} ${xEnd + 8},${y + h / 2} ${xEnd},${y + h + 4} ${xEnd - 8},${y + h / 2}`}
                    fill="var(--pm-warning)"
                    stroke="var(--pm-bg-deep)"
                    pointerEvents="none"
                  />
                )}
              </g>
            );
          })}

          {/* Termin-Streifen unter den AP-Zeilen: Symbol + Titel, auf Bahnen verteilt */}
          {footer.items.map((f) => {
            const y = footerTop + f.lane * FOOTER_LANE_H + 12;
            return (
              <g key={f.key} data-testid={`gantt-${f.kind}`}>
                <title>{f.tooltip}</title>
                {f.kind === 'ms' && (
                  <polygon
                    points={`${f.x - 6},${y - 4} ${f.x},${y - 10} ${f.x + 6},${y - 4} ${f.x},${y + 2}`}
                    fill="var(--pm-warning)"
                    stroke="var(--pm-bg-deep)"
                  />
                )}
                {f.kind === 'mt' && <text x={f.x} y={y} fontSize="13" textAnchor="middle">{f.symbol}</text>}
                {f.kind === 'op' && <text x={f.x} y={y} fontSize="14" textAnchor="middle" fill="var(--pm-error)" fontWeight={700}>✱</text>}
                <text x={f.x + 9} y={y - 1} fontSize="10" fill="var(--pm-fg-muted)">{f.label}</text>
              </g>
            );
          })}

          {/* Footer-Trenner */}
          <line x1={0} y1={footerTop - 4} x2={W} y2={footerTop - 4} stroke="var(--pm-bg-muted)" />
        </svg>
      </Box>
      {onApMove && (
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)', display: 'block', textAlign: 'center', mt: 1, fontStyle: 'italic' }}>
          Tipp: AP-Balken in der Mitte ziehen verschiebt den Zeitraum; an den Kanten ziehen passt Start bzw. Ende tagesgenau an.
        </Typography>
      )}
    </PmCard>
  );
}
